import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { useParams, useNavigate } from "react-router-dom";

import { useAssemblyAI } from "@/hooks/useAssemblyAI";
import { getPresignedUrl } from "@/utils/aws";

import { leadsGetById, leadsChangeLSAStatus, leadUpdateAiCall } from "@/store/leads";
import { leadsCreateReminder, leadsDeleteReminder } from "@/store/leads";
import { repliesGet, repliesCreateACReply } from "@/store/replies";

import AppBox from "@/components/AppBox";
import AppText from "@/components/AppText";
import AppCard from "@/components/AppCard";
import AppButton from "@/components/AppButton";
import AppInput from "@/components/AppInput";

const replaceLeadName = (template: string, leadName: string) => {
  let result = template.replace(/\[LeadName\]/g, leadName || '');
  result = result.replace(/\s*-\s*/g, ' - ').trim();
  return result;
}

const QuickActions: React.FC = () => {
  const dispatch = useAppDispatch();
  const client = useAssemblyAI();
  const navigate = useNavigate();

  const { id: idParam } = useParams<{ id: string }>();

  const { isLoggedIn, me } = useAppSelector((state: any) => state.auth);
  const { lead } = useAppSelector((state: any) => state.leads);
  const { replies, loading } = useAppSelector((state: any) => state.replies);
  const { reminder } = useAppSelector((state: any) => state.alerts);
  const { isMobile } = useAppSelector((state: any) => state.app);

  const [text, setText] = useState('');
  const [loadingAI, setLoadingAI] = useState(false);
  const [isMessageSent, setIsMessageSent] = useState(false);

  useEffect(() => {
    if (idParam) {
      const id = +idParam
      handleFetchLead(id);
    }
  }, [idParam]);

  useEffect(() => {
    if (me.id) {
      dispatch(repliesGet());
    }
  }, [me]);

  useEffect(() => {
    if (me?.company) {
      let currentReply = `Thanks for calling ${me.company}, please let us know if you need any more help.`
      setText(currentReply)
    }
  }, [me]);

  const handleFetchLead = (leadId: number) => {
    dispatch(leadsGetById(leadId));
  };

  const handleChangeInput = (value: string) => {
    setText(value)
  };

  const handleSendNow = () => {
    dispatch(repliesCreateACReply({id: lead.id, data: {text}}))
    setIsMessageSent(true)
  };

  const handleSend24h = () => {
    dispatch(repliesCreateACReply({id: lead.id, data: {text, send24h: true}}))
    setIsMessageSent(true)
  }

  const handleCreateReminder = () => {
    dispatch(leadsCreateReminder({id: lead.id, data: {delay: 1440}})) // set reminder in 24h
  }

  const handleChangeLSAStatus = (status: string) => {
    dispatch(leadsChangeLSAStatus({id: lead.id, data: {status}}))
  }

  const handleChangeAiCall = (data: any) => {
    if (lead?.id) {
      dispatch(
        leadUpdateAiCall({ id: lead.aiCall.id, data })
      );
    }
  };

  const fetchFile = async (key: string) => {
    try {
      const url = await getPresignedUrl(`${key}.mp3`);
      return url
    } catch (error) {
      console.error("Error fetching file", error);
      return null;
    }
  };

  const handleNavigateToLeadPage = () => {
    navigate(`/lead/${lead.id}`);
  }

  const handleProcessCall = async () => {
    setLoadingAI(true);
    try {
      // Generate transcript first
      const fileUrl = await fetchFile(lead.aiCall.audio_key);
      if (fileUrl) {
        const config = {
          audio_url: fileUrl
        };
        
        const transcript = await client.transcripts.transcribe(config);
        
        if (transcript) {          
          // Generate and save summary
          const { response } = await client.lemur.task({
            transcript_ids: [transcript.id.toString()],
            prompt: 'Provide a brief summary of the transcript.',
            final_model: 'anthropic/claude-3-5-sonnet'
          });

          await handleChangeAiCall({
            transcript: transcript.text, 
            transcript_id: transcript.id,
            summary: response
          });
        }
      }
    } catch (error) {
      console.error("Error processing call:", error);
    }
    setLoadingAI(false);
  };

  return (
    <AppBox column fullHeight gap={2} fullWidth>
      {/* Header */}
      <AppBox align="center" gap={4}>
        <AppText variant="h3">
          {`${lead?.name || `LSA ${lead?.type} Lead`}`}
        </AppText>
      </AppBox>

      {/* Main Content */}
      <AppBox column gap={4} className="h-[calc(100vh-140px)] lg:flex-row">
        <AppCard className="divide-y gap-4 overflow-auto">
          {
            lead?.type === 'Answered Call'
              ? <>
                  <AppBox column center gap={2} className="py-8">
                    <AppText variant="h6">Text the Caller</AppText>
                    <AppInput
                      type="text"
                      onChange={(value) => handleChangeInput(value)}
                      value={text}
                      multiline
                      rows={3}
                      fullWidth
                    />
                    {
                      ! isMessageSent
                        ? <>
                            <AppButton onClick={handleSendNow} isLoading={loading}>Send Now</AppButton>
                            <AppButton onClick={handleSend24h}>Send in 24 hours</AppButton>                        
                          </>
                        : <AppBox column gap={4}>
                            <AppText>The message is sent! Click button below to see more</AppText>
                            <AppButton variant="primary" onClick={handleNavigateToLeadPage}>See Lead in our App</AppButton>
                          </AppBox>
                    }
                  </AppBox>
                  
                  <AppBox column center gap={2} className="py-8">
                    <AppText variant="h6">Text me in 24 hours to remind me about this caller.</AppText>
                    {
                      lead?.reminders
                        ? <AppText align="center" className="justify-center">Reminder is set!</AppText>
                        : <AppButton onClick={handleCreateReminder}>Remind me in 24 hours</AppButton>
                    }
                  </AppBox>
                  
                  <AppBox column center gap={2} className="py-8">
                    <AppText variant="h6">Change Lead status inside LSA.</AppText>
                    <AppBox fullWidth gap={4}>
                      <AppButton onClick={() => handleChangeLSAStatus('archived')}>Mark as Archived</AppButton>
                      <AppButton onClick={() => handleChangeLSAStatus('booked')}>Mark as Booked</AppButton>
                    </AppBox>
                  </AppBox>
                  
                  <AppBox column center gap={2} className="py-8">
                    <AppText variant="h6">See the transcription and summary of your call.</AppText>

                    {
                      lead?.aiCall?.audio_key
                        ? <AppBox column gap={4}>
                            {
                              lead?.aiCall?.transcript && lead?.aiCall?.summary
                                ? <>
                                    <AppText>{lead.aiCall.transcript}</AppText>
                                    <AppText>{lead.aiCall.summary}</AppText>
                                  </>
                                : <AppButton 
                                    onClick={handleProcessCall} 
                                    isLoading={loadingAI}
                                    className="w-[160px]"
                                  >
                                    Process Call
                                  </AppButton>
                            }
                          </AppBox>
                        : <AppText>We are still processing your call recording...</AppText>
                    }
                  </AppBox>
                </>
              : null
          }
          
          <AppBox column gap={4} className="pt-4">
            <AppButton variant="primary" onClick={handleNavigateToLeadPage}>See Lead in our App</AppButton>
          </AppBox>
        </AppCard>
      </AppBox>
    </AppBox>
  );
};

export default QuickActions;